import { useEffect, useState } from 'react';

/**
 * The MOIZ brand mark, used in the sidebar rail and on the login card.
 *
 * The artwork is a wide banner (~2:1) that already carries the company name, so it is sized by
 * height with the width left to scale — forcing a square box would letterbox or crop it.
 *
 * The file is served from /public rather than imported, so a missing logo degrades to the "IMS"
 * lettermark instead of failing the build. onError covers the same case at runtime (file absent on
 * the server, or a bad deploy), which keeps the header from collapsing to a broken-image icon.
 */

const LOGO_SRC = '/assets/logo.jpg';

interface Props {
  /**
   * Rendered height in px. Omit to let CSS drive the size — the sidebar does this via
   * `.app-brand .brand-logo` in index.css, so the rail logo can be tuned without a rebuild
   * of this component. An inline height would override that rule, so it is only set when passed.
   */
  height?: number;
}

export default function BrandLogo({ height }: Props) {
  const [failed, setFailed] = useState(false);

  // A failed load used to latch forever: one transient miss (a cold cache, a slow first paint on
  // the Urdu switch) left the "IMS" lettermark in place for the rest of the session even though the
  // file was there. Retrying on the next mount and on re-render clears that.
  useEffect(() => {
    if (!failed) return;
    const retry = window.setTimeout(() => setFailed(false), 1500);
    return () => window.clearTimeout(retry);
  }, [failed]);

  if (failed) {
    const fallbackSize = height ?? 40;
    return (
      <span
        className="app-brand-mark"
      data-no-translate
        style={{ minWidth: fallbackSize, height: fallbackSize, fontSize: Math.round(fallbackSize * 0.4) }}
      >
        IMS
      </span>
    );
  }

  return (
    <img
      className="brand-logo"
      data-no-translate
      src={LOGO_SRC}
      alt="MOIZ Mobile & Corporation"
      onError={() => setFailed(true)}
      style={height ? { height } : undefined}
    />
  );
}
